import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "@/App.css";
import "@/sections.css";
import Constellation from "@/components/Constellation";
import SiteFooter from "@/components/SiteFooter";

const OPS_EMAIL = "Bktoperations@blackknighttrans.com";
const UPDATED = "Effective July 2026";

function LegalShell({ eyebrow, title, children }) {
  return (
    <div className="bk-root" data-testid="legal-page">
      <div className="bk-mesh" />
      <div className="bk-grid-overlay" />
      <Constellation />
      <div className="bk-layer">
        <div className="bk-legal">
          <Link to="/" className="bk-legal-back" data-testid="legal-back-link">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <div className="bk-eyebrow">{eyebrow}</div>
          <h1>{title}</h1>
          <div className="bk-legal-date">{UPDATED} &middot; Black Knight Transportation LLC (Est. 2025)</div>
          {children}
        </div>
        <SiteFooter />
      </div>
    </div>
  );
}

export function Disclaimer() {
  return (
    <LegalShell eyebrow="Compliance" title="Service Disclaimer & FMCSA Positioning">
      <p>
        <strong style={{ color: "#00FF66" }}>
          Black Knight Transportation LLC is a freight dispatch and dispatch-recruiting service.
        </strong>{" "}
        We act as the administrative and back-office command center for independent owner-operators and
        motor carriers. We are <strong style={{ color: "#fff" }}>not</strong> a motor carrier, freight broker,
        or freight forwarder, and we do not take possession of, transport, or assume liability for any freight.
      </p>
      <h2>Our Role</h2>
      <ul>
        <li>We negotiate rates and locate loads on behalf of carriers who hold their own operating authority.</li>
        <li>The carrier remains solely responsible for the physical transportation of all freight.</li>
        <li>All loads are moved under the carrier's own USDOT/MC authority and insurance.</li>
        <li>We do not guarantee specific load volumes, rates, or earnings.</li>
      </ul>
      <h2>Carrier Responsibilities</h2>
      <p>
        Carriers must maintain active operating authority, valid insurance meeting or exceeding federal
        minimums, a satisfactory FMCSA safety rating, and full compliance with all applicable DOT and
        FMCSA regulations. Black Knight Transportation LLC coordinates dispatch only for carriers in good standing.
      </p>
      <h2>No Broker Relationship</h2>
      <p>
        Nothing on this website creates a broker-carrier or shipper-carrier relationship with Black Knight
        Transportation LLC. Rate confirmations and load agreements are executed directly between the carrier
        and the broker/shipper.
      </p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell eyebrow="Legal" title="Privacy Policy">
      <p>
        This Privacy Policy explains how Black Knight Transportation LLC ("we", "us") collects, uses, and
        protects information submitted through this website.
      </p>
      <h2>Information We Collect</h2>
      <ul>
        <li>Contact details you submit (name, email address).</li>
        <li>Carrier information such as MC/DOT numbers and operational background.</li>
        <li>Documents you voluntarily upload (e.g., operating authority, insurance, W-9).</li>
        <li>Anonymous analytics data collected via Google Tag Manager / Google Analytics.</li>
      </ul>
      <h2>How We Use It</h2>
      <ul>
        <li>To evaluate and respond to dispatch and recruiting inquiries.</li>
        <li>To onboard carriers and coordinate dispatch services.</li>
        <li>To improve our website and services.</li>
      </ul>
      <h2>Data Protection</h2>
      <p>
        Rate confirmations, lane analytics, uploaded documents, and communications are stored securely and
        access is restricted to authorized personnel. We do not sell your personal information.
      </p>
      <h2>Cookies & Analytics</h2>
      <p>
        We use Google Tag Manager and analytics cookies to understand site usage. You may disable cookies in
        your browser settings.
      </p>
      <h2>Contact</h2>
      <p>
        For privacy requests, contact <a href={`mailto:${OPS_EMAIL}`} style={{ color: "#00F0FF" }}>{OPS_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell eyebrow="Legal" title="Terms of Service">
      <p>
        By accessing this website or submitting an intake request, you agree to these Terms of Service.
      </p>
      <h2>Services</h2>
      <p>
        Black Knight Transportation LLC provides freight dispatching and dispatch-recruiting services to
        independent owner-operators and motor carriers. We are a dispatch service and not a motor carrier or
        broker (see our Service Disclaimer).
      </p>
      <h2>Eligibility</h2>
      <p>
        Carriers must hold valid operating authority, active insurance, and a satisfactory safety record.
        We reserve the right to decline or terminate service for any carrier not in compliance.
      </p>
      <h2>Fees</h2>
      <p>
        Dispatch services are provided for a transparent percentage of gross load revenue, disclosed prior to
        onboarding. There are no forced-dispatch requirements and service may be cancelled per the terms of
        your dispatch agreement.
      </p>
      <h2>Limitation of Liability</h2>
      <p>
        We are not liable for freight loss, damage, delays, or claims, which remain the responsibility of the
        carrier and its insurers. We make no guarantee of specific load availability, rates, or earnings.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about these terms? Email{" "}
        <a href={`mailto:${OPS_EMAIL}`} style={{ color: "#00F0FF" }}>{OPS_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}
